




import { Component, Vue } from "vue-property-decorator";
import { Scene, WebGLRenderer, PerspectiveCamera, Raycaster, Color, Mesh, AmbientLight, DirectionalLight, Vector2,
 MeshBasicMaterial, BoxBufferGeometry, GridHelper, Object3D, PlaneBufferGeometry, Material } from "three";
import * as THREE from "three";
import { OrbitControls } from '@/three/OrbitControls';
import Brick from '@/three/Brick';
import { environment } from '@/environment';
import { Tool } from './Tool';
import ToolService from '@/services/tool-service';

@Component({
  components: {}
})
export default class Viewport extends Vue {
  private scene = new Scene();
  private mouse = new Vector2();
  private renderer = new WebGLRenderer({ antialias: true });
  private camera = new PerspectiveCamera(45, window.innerWidth / window.innerHeight, 1, 10000);
  private raycaster = new Raycaster();
  private htmlContainer: any = {};
  private plane: any = {};
  private objects: Mesh[] = [];
  private rollOverMesh: Mesh = new Mesh(
    new BoxBufferGeometry(environment.gridUnitWidth, environment.gridUnitHeight, environment.gridUnitLength), 
    new MeshBasicMaterial({color: 0xff0000, opacity: 0.5, transparent: true })
  );
  private canDoAction = true;
  private activeTool = Tool.Add;
  private color = '#c8ac74';

  //////// LifeCycle Hooks ////////

  private mounted() {
    if (process.env.NODE_ENV === "development") {
      (window as any).THREE = THREE;
      (window as any).scene = this.scene;
    }

    ToolService.$on('enableTool', (tool: Tool) => {
      this.activeTool = tool;
      if (tool == Tool.Remove || tool == Tool.Paint)
        this.rollOverMesh.visible = false;
      else
        this.rollOverMesh.visible = true;
    });

    ToolService.$on('changeColor', (color: string) => {
      this.color = color;
    });

    // Create context
    this.scene.name = "Lego World";
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setClearColor(new Color('white'));
    this.renderer.setPixelRatio( window.devicePixelRatio );
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap; 
    this.htmlContainer = document.getElementById("viewport") as HTMLDivElement;
    this.renderer.domElement.id = "canvas";
    this.htmlContainer.appendChild(this.renderer.domElement);

    // Setup camera
    this.camera.position.set(500, 800, 1300);
    this.camera.lookAt(0, 0, 0);
    const controls = new OrbitControls(this.camera, this.renderer.domElement);

    // Follow events
    this.htmlContainer.addEventListener("mousemove", this.onMouseMove, false);
    this.htmlContainer.addEventListener("mouseup", this.onMouseUp, false);
    this.htmlContainer.addEventListener("mousedown", this.onMouseDown, false);
    window.addEventListener("resize", this.onWindowResize, false);

    // Add base geometry
    this.scene.add(new GridHelper(environment.gridWidth, 20));
    this.scene.add(this.rollOverMesh);

    const geometry = new PlaneBufferGeometry(environment.gridWidth, environment.gridLength);
    geometry.rotateX(-Math.PI / 2);
    this.plane = new Mesh(geometry, new MeshBasicMaterial({ visible: false }));
    this.scene.add(this.plane);
    this.objects.push(this.plane);
    
    // Add lights
    this.scene.add(new AmbientLight(0x606060));
    const directionalLight = new DirectionalLight(0xffffff);
    directionalLight.position.set(1, 0.75, 0.5).normalize();
    directionalLight.castShadow = true;
    this.scene.add(directionalLight);

    directionalLight.shadow.mapSize.width = 512;  // default
    directionalLight.shadow.mapSize.height = 512; // default
    directionalLight.shadow.camera.near = 0.5;    // default
    directionalLight.shadow.camera.far = 500;     // default

    // Render loop
    this.animate();
  }

  //////// Private Methods ////////

  private animate() {
    requestAnimationFrame(this.animate);
    this.renderer.render(this.scene, this.camera);
  }

  ///////// Event listeners ////////

  private onMouseMove(event: MouseEvent) {
    const newPosition = new Vector2((event.clientX / window.innerWidth ) * 2 - 1, -(event.clientY / window.innerHeight) * 2 + 1);
    if (Math.abs(this.mouse.x - newPosition.x) > 0.005 || Math.abs(this.mouse.y - newPosition.y) > 0.005)
      this.canDoAction = false;

    this.mouse.set(newPosition.x, newPosition.y);
    this.raycaster.setFromCamera(this.mouse, this.camera);
    const intersects = this.raycaster.intersectObjects(this.objects);

    if (intersects.length > 0) {
      const intersect = intersects[0];
      if (!intersect.face)
        return;

      this.rollOverMesh.position.copy(intersect.point).add(intersect.face.normal);
      this.rollOverMesh.position.divideScalar(environment.gridUnitWidth)
        .floor()
        .multiplyScalar(environment.gridUnitWidth)
        .addScalar(environment.gridUnitWidth / 2);
    }
  }

  private onMouseDown(event: MouseEvent) {
    this.canDoAction = true;
  }

  private onMouseUp(event: MouseEvent) {
    this.mouse.set((event.clientX / window.innerWidth) * 2 - 1, - (event.clientY / window.innerHeight) * 2 + 1 );
    this.raycaster.setFromCamera(this.mouse, this.camera);
    const intersects = this.raycaster.intersectObjects(this.objects);

    if (intersects.length > 0 && this.canDoAction && event.button == 0) {
      const intersect = intersects[ 0 ];
      if (!intersect.face)
        return;

      switch (this.activeTool) {
        case Tool.Add: {
          const voxel = new Brick(1, 1, this.color).getMesh();
          voxel.position.copy(intersect.point).add(intersect.face.normal);
          voxel.position.divideScalar(environment.gridUnitWidth)
            .floor()
            .multiplyScalar(environment.gridUnitWidth)
            .addScalar(environment.gridUnitWidth / 2);
          this.scene.add(voxel);
          this.objects.push(voxel);
          break;
        }

        case Tool.Remove: {
          if (intersect.object !== this.plane) {
            this.scene.remove(intersect.object);
            this.objects.splice(this.objects.indexOf(intersect.object as any), 1);
          }
          break;
        }

        case Tool.Paint: {
          if (intersect.object !== this.plane) {
            const index = this.objects.indexOf(intersect.object as any);
            const material = this.objects[index].material as any;
            material.setValues({ color: this.color });
          }
          break;
        }

        default: break;
      }
    }
  }

  private onWindowResize() {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  }
}
